import { zodResolver } from "@hookform/resolvers/zod";
import {
  ArrowUpRightIcon,
  CheckIcon,
  ChevronDownIcon,
  XIcon,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { SectionStatusIndicator } from "./SectionStatusIndicator";
import { Column, Row, Text } from "@/components";
import { ConfigFormWrapper } from "@/components/companies/shared/ConfigFormWrapper";
import { FormEnumSelect } from "@/components/form/FormEnumSelect";
import { FormNumberSelect } from "@/components/form/FormNumberSelect";
import { FormSwitchInput } from "@/components/form/FormSwitchInput";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleTrigger } from "@/components/ui/collapsible";

import { useTaggableUsers, useUserOptionsWithSelected } from "@/contexts/TaggableUsersContext";
import { useJobInternalConfigSubmit } from "@/hooks/updateSets/useJobInternalConfigSubmit";
import {
  jobCategoryOptions,
  jobCategoryToLabel,
  MAX_APR_RATING,
  stevieOutreachMaxRatingOptions,
} from "@/lib/constants";
import { env } from "@/lib/env";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import {
  JobCategory,
  JobInternalConfig as JobInternalConfigType,
} from "@/types";

export const GeneralSection = ({
  jobInternalConfig,
}: {
  jobInternalConfig: JobInternalConfigType;
}) => {
  /* Render */
  const render = () => {
    return (
      <SectionStatusIndicator
        complete={
          jobInternalConfig.ownerId !== null &&
          jobInternalConfig.category !== null &&
          jobInternalConfig.isWorkableJobPosted &&
          jobInternalConfig.isSponsoredOnIndeed
        }
      >
        <Collapsible className="group/collapsible">
          <Column className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <CollapsibleTrigger className="flex w-full cursor-pointer items-center justify-between px-6 py-4">
              <Text variant="header">General</Text>
              <GeneralTriggerBadges jobInternalConfig={jobInternalConfig} />
            </CollapsibleTrigger>
            <GeneralForm jobInternalConfig={jobInternalConfig} />
          </Column>
        </Collapsible>
      </SectionStatusIndicator>
    );
  };

  return render();
};

const GeneralForm = ({
  jobInternalConfig,
}: {
  jobInternalConfig: JobInternalConfigType;
}) => {
  /* Context */
  const userOptions = useUserOptionsWithSelected([jobInternalConfig.ownerId]);

  /* Database CRUD Operations */
  // Update
  const { handleSubmit, isPending: isSaving } = useJobInternalConfigSubmit(
    jobInternalConfig.id,
    "General config saved!",
  );

  const generalSchema = z.object({
    ownerId: z.number().nullable().optional(),
    isAgentFlowEnabled: z.boolean(),
    stevieOutreachMaxRating: z.number().int().min(1).max(MAX_APR_RATING),
    isWorkableJobPosted: z.boolean(),
    isSponsoredOnIndeed: z.boolean(),
    category: z.nativeEnum(JobCategory, {
      required_error: "Job category is required",
    }),
  });

  const form = useForm({
    defaultValues: {
      ownerId: jobInternalConfig.ownerId ?? undefined,
      isAgentFlowEnabled: jobInternalConfig.isAgentFlowEnabled,
      stevieOutreachMaxRating: jobInternalConfig.stevieOutreachMaxRating,
      isWorkableJobPosted: jobInternalConfig.isWorkableJobPosted,
      isSponsoredOnIndeed: jobInternalConfig.isSponsoredOnIndeed,
      category: jobInternalConfig.category ?? undefined,
    },
    resolver: zodResolver(generalSchema),
  });

  /* Render */
  return (
    <ConfigFormWrapper
      buttonLabel="Save General Config"
      disabled={isSaving}
      form={form}
      onSubmit={(data) => {
        mixpanelUtils.trackFormSubmission("Job internal config general", MIXPANEL_SCREENS.JOB);
        handleSubmit(data);
      }}
    >
      <Row className="gap-4">
        <FormNumberSelect
          className="flex-1"
          fieldName="ownerId"
          form={form}
          infoTooltip={
            "The user who owns this job."
          }
          label="Job Owner"
          options={userOptions}
        />
        <FormEnumSelect
          className="flex-1"
          fieldName="category"
          form={form}
          infoTooltip="Used for Laborup analytics and tracking company-wide progression. Also included in Mixpanel events for text and voice AI outreach status changes."
          label="Job Category"
          options={jobCategoryOptions}
          placeholder="Select a category"
        />
      </Row>
      <Row className="gap-4">
        <FormSwitchInput
          className="flex-1"
          fieldName="isAgentFlowEnabled"
          form={form}
          infoTooltip="Enable Stevie to automatically reach out to candidates"
          label="Agentic Staffing Enabled"
        />
        <FormSwitchInput
          className="flex-1"
          fieldName="isWorkableJobPosted"
          form={form}
          infoTooltip={
            <Column className="gap-2">
              <Text>Mark this job as posted on Workable</Text>
              <Button
                className="h-auto justify-start gap-1 p-0 text-xs font-medium text-primary-600 hover:bg-transparent hover:underline"
                variant="ghost"
                onClick={() => {
                  void navigator.clipboard.writeText(jobInternalConfig.id);
                  window.open(env.getWorkableNewJobFormUrl());
                }}
              >
                <ArrowUpRightIcon className="h-3 w-3" />
                Create Workable Job
              </Button>
            </Column>
          }
          label="Workable Job Posted"
        />
        <FormSwitchInput
          className="flex-1"
          fieldName="isSponsoredOnIndeed"
          form={form}
          infoTooltip="Mark this job as sponsored on Indeed"
          label="Sponsored on Indeed"
        />
      </Row>
      <Row className="gap-4">
        <FormNumberSelect
          className="flex-1"
          disabled={!form.watch("isAgentFlowEnabled")}
          fieldName="stevieOutreachMaxRating"
          form={form}
          infoTooltip="The highest APR rating Stevie will reach out to. Candidates rated above it are left to a human recruiter, so the default of 3 stars and below skips 4-5 star candidates."
          label="Stevie Outreach Rating Ceiling"
          options={stevieOutreachMaxRatingOptions}
        />
      </Row>
    </ConfigFormWrapper>
  );
};

const GeneralTriggerBadges = ({
  jobInternalConfig,
}: {
  jobInternalConfig: JobInternalConfigType;
}) => {
  const { userNameMap } = useTaggableUsers();

  return (
    <Row className="items-center gap-2">
      <div className="relative min-w-0 max-w-[500px]">
        <Row className="no-scrollbar items-center gap-2 overflow-x-auto">
          {jobInternalConfig.category ? (
            <Badge variant="secondary" className="shrink-0 rounded-md">
              {jobCategoryToLabel[jobInternalConfig.category]}
            </Badge>
          ) : (
            <ErrorBadge>No Category</ErrorBadge>
          )}
          {jobInternalConfig.ownerId ? (
            <SuccessBadge>
              Owner: {userNameMap[jobInternalConfig.ownerId] ?? "Unknown"}
            </SuccessBadge>
          ) : (
            <ErrorBadge>No Owner</ErrorBadge>
          )}
          {jobInternalConfig.isWorkableJobPosted ? (
            <SuccessBadge>
              Workable: <CheckIcon className="h-4 w-4" />
            </SuccessBadge>
          ) : (
            <ErrorBadge>
              Workable: <XIcon className="h-4 w-4" />
            </ErrorBadge>
          )}
          {jobInternalConfig.isSponsoredOnIndeed ? (
            <SuccessBadge>
              Indeed: <CheckIcon className="h-4 w-4" />
            </SuccessBadge>
          ) : (
            <ErrorBadge>
              Indeed: <XIcon className="h-4 w-4" />
            </ErrorBadge>
          )}
          {jobInternalConfig.isAgentFlowEnabled ? (
            <SuccessBadge>Stevie: On</SuccessBadge>
          ) : (
            <Badge className="shrink-0 border-slate-200 bg-slate-50 text-slate-400">
              Stevie: Off
            </Badge>
          )}
        </Row>
        <div className="pointer-events-none absolute right-0 top-0 h-full w-12 bg-gradient-to-l from-white to-transparent" />
      </div>
      <ChevronDownIcon className="h-4 w-4 text-slate-500 transition-transform duration-200 group-data-[state=closed]/collapsible:-rotate-90" />
    </Row>
  );
};

const SuccessBadge = ({ children }: { children: React.ReactNode }) => {
  return (
    <Badge className="shrink-0 rounded-md border-emerald-200 bg-emerald-50 text-emerald-600">
      {children}
    </Badge>
  );
};

const ErrorBadge = ({ children }: { children: React.ReactNode }) => {
  return (
    <Badge className="shrink-0 rounded-md border-red-200 bg-red-50 text-red-600">
      {children}
    </Badge>
  );
};
